import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { useToast } from '../../lib/use-toast';

const STORAGE_KEY = 'courseProgress';

// Fonction pour obtenir le numéro du dernier chapitre complété
const getLastCompletedChapter = (courseId) => {
  try {
    const progress = localStorage.getItem(STORAGE_KEY);
    if (!progress) return 0;
    
    const progressData = JSON.parse(progress);
    return progressData[courseId] || 0;
  } catch (error) {
    console.error('Erreur lors de la lecture de la progression:', error);
    return 0;
  }
};

// Fonction pour sauvegarder le numéro du chapitre complété
const saveProgress = (courseId, chapterNumber) => {
  try {
    console.log('Sauvegarde de la progression:', { courseId, chapterNumber });
    
    // Récupérer la progression existante ou créer un nouvel objet
    const savedProgress = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    
    // Vérifier si la nouvelle progression n'est pas inférieure à l'ancienne
    const currentProgress = savedProgress[courseId] || -1;
    if (chapterNumber < currentProgress) {
      console.log('Tentative de régression de progression ignorée');
      return true; // On retourne true car ce n'est pas une erreur
    }
    
    // Mettre à jour la progression avec le numéro du chapitre
    savedProgress[courseId] = chapterNumber;
    
    // Sauvegarder dans localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(savedProgress));
    console.log('Nouvelle progression sauvegardée:', savedProgress);
    
    return true;
  } catch (error) {
    console.error('Erreur lors de la sauvegarde de la progression:', error);
    return false;
  }
};

export const CourseNavigation = ({ courseId, chapterId, getNextChapter, getPreviousChapter }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const nextChapter = getNextChapter(chapterId);
  const prevChapter = getPreviousChapter(chapterId);

  // Extraire le numéro du chapitre actuel
  const getCurrentChapterNumber = (chapterId) => {
    if (chapterId === 'introduction') {
      return 0; // Le chapitre d'introduction est considéré comme le chapitre 0
    } else if (chapterId.startsWith('chapitre')) {
      return parseInt(chapterId.replace('chapitre', ''));
    } else if (chapterId.startsWith('exercice')) {
      // Pour les exercices, on peut utiliser une logique différente
      return parseInt(chapterId.replace('exercice', ''));
    }
    return 0; // Valeur par défaut
  };

  const currentChapterNumber = getCurrentChapterNumber(chapterId);

  // Effet pour scroller en haut quand on change de chapitre
  useEffect(() => {
    // Ne pas scroller si l'URL contient une ancre
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]); // Déclencher uniquement sur le changement de pathname, pas de hash

  // Marquer le chapitre comme complété et passer au suivant
  const handleComplete = async () => {
    try {
      console.log('Tentative de sauvegarde du chapitre', currentChapterNumber);
      const saved = saveProgress(courseId, currentChapterNumber);
      if (!saved) {
        throw new Error("Erreur lors de la sauvegarde de la progression");
      }
      
      // Naviguer vers le prochain chapitre s'il existe
      if (nextChapter) {
        console.log('Navigation vers le prochain chapitre:', nextChapter.path);
        
        // Utiliser navigate avec replace:true pour éviter d'ajouter à l'historique
        navigate(nextChapter.path, { replace: true });
        
        // Scroller en haut de page après navigation
        window.scrollTo(0, 0);
      } else {
        console.log('Fin du cours atteinte');
        toast({
          title: "Félicitations !",
          description: "Vous avez terminé le cours !",
          variant: "success",
        });
        // Rediriger vers la page des cours après un court délai
        setTimeout(() => {
          navigate('/courses', { replace: true });
        }, 100);
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la progression:", error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour la progression.",
        variant: "destructive",
      });
    }
  };

  // Naviguer vers le chapitre précédent
  const handlePrevious = () => {
    if (prevChapter) {
      // Simplement naviguer vers le chapitre précédent sans référencer steps
      navigate(prevChapter.path);
    }
  };

  return (
    <div className="flex justify-between mt-8">
      <Button
        variant="outline"
        onClick={handlePrevious}
        disabled={!prevChapter}
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className="h-5 w-5 mr-1" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Précédent
      </Button>
      
      <div className="flex items-center space-x-2">
        <Button
          variant="default"
          onClick={() => {
            console.log('Bouton Suivant cliqué');
            console.log('Valeurs actuelles:', { courseId, chapterId, nextChapter });
            handleComplete();
          }}
          disabled={false}
        >
          {nextChapter ? (
            <>
              Suivant
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5 ml-1" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </>
          ) : (
            "Terminer"
          )}
        </Button>
      </div>
    </div>
  );
}; 